import { Component, createSignal, For, Match, onMount, Show, Switch } from "solid-js"
import { createStore } from "solid-js/store";
import mediumZoom from "medium-zoom";
import 'medium-zoom/dist/style.css';
import { fetchGet, cleanImagePath } from "../../API";
import { PageList } from "../page-list";
import { TimelineSearch, TimelineFilter } from "../../Types";

type TimelineProps = {
    filter: TimelineFilter;
    onLoadNewTab: (name: string, filter: TimelineFilter) => Promise<void>;
}
type State = {
    editFromYear: boolean;
    editToYear: boolean;
    filterByArtist: boolean;
}
type Resources = {
    Pages: number[]
}
type CalendarDateProps = {
    year: string;
    month: string;
    day: string;
}
const months = ["January","February","March","April","May","June","July","August","September", "October", "November", "December"];
export const CalendarDate : Component<CalendarDateProps> = ({ year, month, day }) => {
    return (
        <>
            <Show when={year}>
                <Switch>
                    <Match when={month && !day}>
                        <span style="font-size: 1rem;">{months[parseInt(month) - 1]}</span>
                    </Match>
                    <Match when={month && day}>
                        <span style="font-size: 1rem;">{months[parseInt(month) - 1]} {day}</span>
                    </Match>
                </Switch>
                <div style="font-size: 2.25rem;">
                    {year}
                </div>
            </Show>
        </>
    )
}

export const Timeline : Component<TimelineProps> = (props) => {
    const sm = 125;
    const lg = 800;
    const [search, setSearch] = createStore<TimelineSearch>({
        Filter: {
            FromYear: "",
            ToYear: ""
        }
    } as TimelineSearch);
    const [resources, setResources] = createStore<Resources>({} as Resources);
    const [page,setPage] = createSignal<number>(1);
    const [maxPage,setMaxPage] = createSignal<number>(1);
    const [blocksize,setBlocksize] = createSignal<number>(20);
    const loadEvents = async () => {
        const res = await fetchGet("/Artist/SearchTimelineEventsJson", {
            ArtistId: search.Filter.ArtistId || "",
            FromYear: search.Filter.FromYear || "",
            ToYear: search.Filter.ToYear || "",
            Page: page()
        });
        const json = await res.json();
        if (!json.Success){
            return;
        }
        setPage(json.Data.Page);
        setMaxPage(json.Data.MaxPage);
        setSearch(json.Data);
        setResources("Pages", generatePages());
        mediumZoom(".zoomable");
    };
    const generatePages = () => {
        const list = [];
        for (let p = 0; p < search.MaxPage; p++) {
            list.push(p);
        }
        return list;
    }
    const updatePageNumber = async (p: number) => {
        setPage(p);
        await loadEvents();
    }
    // const setRanges = async () => {
    //     const res = await fetchGet("/Artist/GetTimelineEventRangesJson", { id: props.filter.ArtistId });
    //     const json = await res.json();
    //     if (!json.Success) return;
    //     setSearch("Filter", "FromYear", json.Data.FromYear);
    //     setSearch("Filter", "ToYear", json.Data.ToYear);
    // }
    const toggleRestrictToArtist = async () => {
        setState("filterByArtist", !state.filterByArtist);
        setSearch("Filter", "ArtistId", state.filterByArtist ? props.filter.ArtistId : "");
        setSearch("Page", 1);
        setPage(1);
        setMaxPage(1);
        await loadEvents();
    }
    const [state, setState] = createStore<State>({
        filterByArtist: true
    } as State);
    const loadIntoNewTab = async (filter: TimelineFilter) => {
        let name = "<unknown>";
        if (filter.FromYear && (!filter.ToYear || filter.ToYear == filter.FromYear)) {
            name = "In " + filter.FromYear;
        }
        if (filter.FromYear && filter.ToYear && (filter.FromYear != filter.ToYear)) {
            name = "From " + filter.FromYear + " to " + filter.ToYear;
        }
        await props.onLoadNewTab(name, filter);
    }
    onMount(async () => {
        if (props.filter.ArtistId) {
            setSearch("Filter", "ArtistId", props.filter.ArtistId);
        }
        if (props.filter.FromYear) {
            setSearch("Filter", "FromYear", props.filter.FromYear);
        }
        if (props.filter.ToYear) {
            setSearch("Filter", "ToYear", props.filter.ToYear);
        }
        // await setRanges();
        await loadEvents();
    });
    return (
        <>
            <div class="container">
                <div class="timeline-container">
                    <Show when={props.filter.ArtistId}>
                        <div>
                            <input
                                type="checkbox"
                                checked={state.filterByArtist}
                                onInput={toggleRestrictToArtist}
                            /> Show only events concerning {props.filter?.ArtistName || "the artist"}
                        </div>
                    </Show>
                    <h4>
                        {search?.Count?.toLocaleString()} events between 
                        <span>
                            <Show when={state.editFromYear}>
                                <input
                                    type="text"
                                    class="form-control timeline-year-input mr-5"
                                    value={search.Filter.FromYear}
                                    onChange={async (e) => { setState("editFromYear", false); setSearch("Filter", "FromYear", e.currentTarget.value); await loadEvents(); }} />
                            </Show>
                            <Show when={!state.editFromYear}>
                                <span class="timeline-year-label" style="margin-right: 5px;" onClick={() => setState("editFromYear", true)}>
                                    {search.Filter.FromYear}        
                                </span>
                            </Show>
                        </span>
                        and
                        <span>
                            <Show when={state.editToYear}>
                                <input
                                    type="text"
                                    class="form-control timeline-year-input ml-5"
                                    value={search.Filter.ToYear}
                                    onChange={async (e) =>  { setState("editToYear", false); setSearch("Filter", "ToYear", e.currentTarget.value); await loadEvents(); }} />
                            </Show>
                            <Show when={!state.editToYear}>
                                <span class="timeline-year-label" style="margin-left: 5px;" onClick={() => setState("editToYear", true)}>
                                    {search.Filter.ToYear}        
                                </span>
                            </Show>
                        </span>
                    </h4>
                    <div>
                        Page {page()?.toLocaleString()} of {maxPage()?.toLocaleString()}
                    </div>
                    <div class="pagelist-container">
                        <PageList page={page} maxPage={maxPage} blockSize={blocksize} onClick={(p) => updatePageNumber(p)} />
                    </div>
                    <table class="table table-condensed">
                        <thead>
                            <tr>
                                <th class="image">

                                </th>
                                <th class="dates">
                                    
                                </th>
                                <th>
                                    
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={search.Results}>{(event) =>
                                {
                                    return <tr classList={{ ["event-highlight"]: search.Filter.ArtistId == "" && event.Artist?.Id == props.filter.ArtistId }}>
                                        <td class="image" style="text-align: center;">
                                            <Show when={event.Artist && !event.Artwork && event.Artist.PrimaryImage}>
                                                <img
                                                    style={`max-height: 125px; max-width: 125px;`}
                                                    src={cleanImagePath(event.Artist.PrimaryImage.URL) + "?mode=crop&format=JPEG&maxWidth=650&maxHeight=650"}
                                                    class="zoomable"
                                                />
                                            </Show>
                                            <Show when={event.Artwork && event.Artwork.PrimaryImage}>
                                                <img
                                                    style={`max-height: 125px; max-width: 125px;`}
                                                    src={cleanImagePath(event.Artwork.PrimaryImage.URL) + "?mode=crop&format=JPEG&maxWidth=650&maxHeight=650"}
                                                    class="zoomable"
                                                />
                                            </Show>
                                        </td>
                                        <td class="dates">
                                            <span onClick={(e) => { e.preventDefault(); loadIntoNewTab({ FromYear: event.StartYear, ToYear: event.StartYear })}}>
                                                <CalendarDate year={event.StartYear} month={event.StartMonth} day={event.StartDay} />
                                            </span>
                                            <Show when={event.EndYear}>
                                                <div onClick={(e) => { e.preventDefault(); loadIntoNewTab({ FromYear: event.StartYear, ToYear: event.EndYear })}}>
                                                    to <CalendarDate year={event.EndYear} month={event.EndMonth} day={event.EndDay} />
                                                </div>
                                            </Show>
                                        </td>
                                        <td class="description">
                                            <div innerHTML={event.Description} style="font-size: 1.5rem;" />
                                            <Show when={event.Images?.length}>
                                                <div>
                                                    <For each={event.Images}>{(image) =>
                                                        <img
                                                            data-mode="thumbnail"
                                                            class="zoomable"
                                                            style={`max-height: ${sm}px; max-width: ${sm}px;margin: 5px;`}
                                                            src={cleanImagePath(image.URL) + `?mode=crop&format=JPEG&maxWidth=${lg}&maxHeight=${lg}&anchor=topLeft`}
                                                        />
                                                    }</For>
                                                </div>
                                            </Show>
                                        </td>
                                    </tr>
                                }
                            }</For>
                        </tbody>
                    </table>
                    <div class="pagelist-container">
                        <PageList page={page} maxPage={maxPage} blockSize={blocksize} onClick={(p) => updatePageNumber(p)} />
                    </div>
                </div>
            </div>
        </>
    )
}